import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  DownloadSimple, Users, TrendUp, Warning, Sparkle,
  MagnifyingGlass, CaretDown, CaretUp, EnvelopeSimple, Eye, ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (n) => (n == null ? "—" : `₹${Math.round(Number(n)).toLocaleString("en-IN")}`);
const fmtL = (n) => {
  if (n == null) return "—";
  const v = Number(n);
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
};

const NEXT_MONTH_YM = (() => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
})();

const MONTH_LABEL = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

const SOURCE_BADGE = {
  elected_new_regime: { label: "Elected · New", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  elected_old_regime: { label: "Elected · Old", color: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  flat_tds_percent: { label: "Flat tds%", color: "bg-amber-100 text-amber-800 border-amber-200" },
};

export default function TaxDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  // Nudge state
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [nudgeDry, setNudgeDry] = useState(null);
  const [nudgeLoading, setNudgeLoading] = useState(false);
  const [nudgeResult, setNudgeResult] = useState(null);

  // Payroll preview state
  const [previewMonth, setPreviewMonth] = useState(NEXT_MONTH_YM);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/tax/dashboard").then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load tax dashboard"))
      .finally(() => setLoading(false));
  }, []);

  const loadPreview = () => {
    setPreviewLoading(true);
    setPreviewData(null);
    api.get(`/payroll/preview?period_month=${previewMonth}`)
      .then(({ data }) => setPreviewData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setPreviewLoading(false));
  };

  const openNudgeDialog = async () => {
    setNudgeResult(null);
    setNudgeLoading(true);
    try {
      const { data } = await api.post("/tax/nudge-pending", { dry_run: true });
      setNudgeDry(data);
      setNudgeOpen(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setNudgeLoading(false);
  };

  const confirmNudge = async () => {
    setNudgeLoading(true);
    try {
      const { data } = await api.post("/tax/nudge-pending", {});
      setNudgeResult(data);
      toast.success(`Sent ${data.sent} nudge email${data.sent === 1 ? "" : "s"} · ${data.failed} failed · ${data.skipped_no_email} skipped`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setNudgeLoading(false);
  };

  const rows = useMemo(() => {
    if (!data) return [];
    return (data.rows || []).filter((r) => {
      if (filter === "new" && r.regime !== "new") return false;
      if (filter === "old" && r.regime !== "old") return false;
      if (filter === "not_elected" && r.regime) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(r.employee_name || "").toLowerCase().includes(s)
            && !(r.employee_code || "").toLowerCase().includes(s)
            && !(r.department || "").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [data, filter, search]);

  const totals = useMemo(() => {
    const sum = (k) => rows.reduce((a, r) => a + (r[k] || 0), 0);
    return {
      annual_gross: sum("annual_gross"),
      total_deductions: sum("total_deductions"),
      taxable_income: sum("taxable_income"),
      annual_tax: sum("annual_tax"),
      monthly_tds: sum("monthly_tds"),
      ytd_tds: sum("ytd_tds"),
    };
  }, [rows]);

  const exportCSV = () => {
    if (!rows.length) return;
    const headers = [
      "Code", "Name", "Department", "Regime", "Elected on",
      "Annual gross", "Deductions used", "Taxable income", "Annual tax", "Monthly TDS", "YTD TDS",
      "80C", "80CCD(1B)", "80CCD(2)", "80D self", "80D parents", "80E", "80G", "80GG",
      "80TTA/TTB", "80EEA", "80DDB", "80DD", "80U", "24(b)",
      "HRA exempt", "LTA exempt", "Other exempt", "Family pension",
    ];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const csv = [headers.join(",")];
    for (const r of rows) {
      const d = r.declarations || {};
      csv.push([
        r.employee_code, r.employee_name, r.department,
        r.regime || "not_elected",
        r.elected_at ? r.elected_at.slice(0, 10) : "",
        r.annual_gross, r.total_deductions, r.taxable_income, r.annual_tax, r.monthly_tds, r.ytd_tds,
        d.d80c || 0, d.d80ccd_1b || 0, d.d80ccd_2 || 0,
        d.d80d_self || 0, d.d80d_parents || 0,
        d.d80e || 0, d.d80g || 0, d.d80gg || 0, d.d80tta || 0,
        d.d80eea || 0, d.d80ddb || 0, d.d80dd || 0, d.d80u || 0, d.d24b || 0,
        d.hra_exempt || 0, d.lta_exempt || 0, d.other_exempt || 0,
        d.family_pension_deduction || 0,
      ].map(escape).join(","));
    }
    const blob = new Blob([csv.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `tax-dashboard-fy${data.fy}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} rows`);
  };

  if (loading || !data) {
    return <div className="border border-zinc-200 bg-white p-8 rounded-sm text-sm text-zinc-500 text-center">Loading tax dashboard…</div>;
  }

  const s = data.stats;

  return (
    <div className="space-y-6 max-w-7xl">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Payroll · Tax deductions · FY {data.fy}</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Tax dashboard</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Every active employee's regime election, declared amounts, and forecasted monthly TDS.
          Payroll runs use these figures to deduct tax at source · {data.statute}
        </p>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Active employees" value={s.total} icon={Users} testid="kpi-total" />
        <KPI label="Elected — New" value={s.elected_new} icon={Sparkle} accent="emerald" testid="kpi-new"
          sub={`${s.total ? Math.round(s.elected_new * 100 / s.total) : 0}% of active`} />
        <KPI label="Elected — Old" value={s.elected_old} icon={Sparkle} accent="indigo" testid="kpi-old"
          sub={`${s.total ? Math.round(s.elected_old * 100 / s.total) : 0}% of active`} />
        <KPI label="Not elected" value={s.not_elected} icon={Warning} accent="amber" testid="kpi-pending"
          sub={`${s.election_completion_percent}% completion`} />
        <KPI label="Total monthly TDS" value={fmtL(s.total_monthly_tds)} icon={TrendUp} accent="emerald" testid="kpi-tds"
          sub={`Annual ${fmtL(s.total_annual_tax)}`} />
      </div>

      {/* Filter chips + search + nudge + export */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex rounded-sm border border-zinc-200 bg-white p-1 text-sm" data-testid="tax-filter-chips">
          {[
            { key: "all", label: `All (${s.total})` },
            { key: "new", label: `New (${s.elected_new})` },
            { key: "old", label: `Old (${s.elected_old})` },
            { key: "not_elected", label: `Pending (${s.not_elected})` },
          ].map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              data-testid={`tax-filter-${f.key}`}
              className={`px-3 py-1 rounded-sm transition ${filter === f.key ? "bg-adb-navy text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Search employee, code, department…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 max-w-xs rounded-sm" data-testid="tax-search" />
        </div>
        {s.not_elected > 0 && (
          <Button onClick={openNudgeDialog} disabled={nudgeLoading}
            className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white ml-auto"
            data-testid="tax-nudge-pending-btn">
            <EnvelopeSimple size={14} className="mr-1.5" />
            {nudgeLoading ? "Preparing…" : `Nudge ${s.not_elected} pending`}
          </Button>
        )}
        <Button onClick={exportCSV} variant="outline" disabled={rows.length === 0}
          className={`rounded-sm ${s.not_elected === 0 ? "ml-auto" : ""}`} data-testid="tax-export-csv">
          <DownloadSimple size={14} className="mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 rounded-sm bg-white overflow-x-auto" data-testid="tax-dashboard-table">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200 text-xs text-zinc-500 uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 w-8"></th>
              <th className="text-left px-3 py-2">Employee</th>
              <th className="text-left px-3 py-2">Regime</th>
              <th className="text-right px-3 py-2">Annual gross</th>
              <th className="text-right px-3 py-2">Deductions</th>
              <th className="text-right px-3 py-2">Taxable</th>
              <th className="text-right px-3 py-2">Annual tax</th>
              <th className="text-right px-3 py-2">Monthly TDS</th>
              <th className="text-right px-3 py-2">YTD TDS</th>
              <th className="text-left px-3 py-2">Elected</th>
            </tr>
          </thead>
          <tbody className="font-mono-plex">
            {rows.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-zinc-500 italic font-sans">No employees match the current filter.</td></tr>
            ) : rows.map((r) => (
              <TaxRow key={r.employee_id} r={r}
                expanded={!!expanded[r.employee_id]}
                onToggle={() => setExpanded({ ...expanded, [r.employee_id]: !expanded[r.employee_id] })} />
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-zinc-50 border-t-2 border-zinc-200 font-medium">
              <tr>
                <td />
                <td colSpan={2} className="px-3 py-2 uppercase tracking-widest text-[10px] text-zinc-500 font-sans">
                  Total ({rows.length} employees)
                </td>
                <td className="px-3 py-2 text-right font-mono-plex">{fmt(totals.annual_gross)}</td>
                <td className="px-3 py-2 text-right font-mono-plex text-zinc-500">{fmt(totals.total_deductions)}</td>
                <td className="px-3 py-2 text-right font-mono-plex">{fmt(totals.taxable_income)}</td>
                <td className="px-3 py-2 text-right font-mono-plex">{fmt(totals.annual_tax)}</td>
                <td className="px-3 py-2 text-right font-mono-plex text-emerald-800">{fmt(totals.monthly_tds)}</td>
                <td className="px-3 py-2 text-right font-mono-plex text-zinc-500">{fmt(totals.ytd_tds)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="text-xs text-zinc-500 space-y-1">
        <div>• Employees who haven't elected yet are shown with their default (new regime) tax computed as a preview reference.</div>
        <div>• Monthly TDS is prorated to the attendance ratio at payroll run time.</div>
        <div>• Values are computed on the annualized monthly gross of each employee's latest salary structure × 12.</div>
      </div>

      {/* Payroll preview section */}
      <PayrollPreviewSection
        month={previewMonth} setMonth={setPreviewMonth}
        onLoad={loadPreview} loading={previewLoading}
        preview={previewData} />

      {/* Nudge dialog */}
      <NudgeDialog open={nudgeOpen} onOpenChange={setNudgeOpen}
        dry={nudgeDry} result={nudgeResult} loading={nudgeLoading}
        onConfirm={confirmNudge} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nudge confirmation dialog
// ---------------------------------------------------------------------------
function NudgeDialog({ open, onOpenChange, dry, result, loading, onConfirm }) {
  if (!dry) return null;
  const recipients = dry.recipients || [];
  const withEmail = recipients.filter(r => r.email);
  const withoutEmail = recipients.filter(r => !r.email);
  const done = !!result;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="nudge-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <EnvelopeSimple size={20} className="text-amber-600" />
            {done ? "Nudge sent" : `Send tax-declaration nudge to ${withEmail.length} employees?`}
          </DialogTitle>
          <DialogDescription>
            {done ? (
              <>Emailed {result.sent} employees via Resend. {result.failed > 0 && `${result.failed} failed. `}{result.skipped_no_email > 0 && `${result.skipped_no_email} had no email on file.`}</>
            ) : (
              <>Each recipient gets a branded email prompting them to compare regimes and elect for FY {dry.fy}. {withoutEmail.length > 0 && `${withoutEmail.length} employee${withoutEmail.length === 1 ? " has" : "s have"} no email on file and will be skipped.`}</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-64 overflow-y-auto border border-zinc-200 rounded-sm bg-zinc-50/50">
          <table className="w-full text-xs">
            <thead className="bg-zinc-100 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-2 py-1.5">Name</th>
                <th className="text-left px-2 py-1.5">Email</th>
                <th className="text-left px-2 py-1.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {(done ? result.recipients : recipients).map((r, i) => (
                <tr key={r.employee_id + i} className="border-t border-zinc-100" data-testid={`nudge-recipient-${r.employee_id}`}>
                  <td className="px-2 py-1.5">{r.name}</td>
                  <td className="px-2 py-1.5 font-mono-plex text-[11px] text-zinc-600">{r.email || <span className="italic text-amber-700">no email</span>}</td>
                  <td className="px-2 py-1.5">
                    <StatusBadge status={r.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
            className="rounded-sm" data-testid="nudge-cancel-btn">
            {done ? "Close" : "Cancel"}
          </Button>
          {!done && withEmail.length > 0 && (
            <Button onClick={onConfirm} disabled={loading}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
              data-testid="nudge-confirm-btn">
              <EnvelopeSimple size={14} className="mr-1.5" />
              {loading ? "Sending…" : `Send ${withEmail.length} nudge email${withEmail.length === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status }) {
  const map = {
    dry_run: { label: "Ready", cls: "bg-zinc-100 text-zinc-700" },
    sent: { label: "Sent", cls: "bg-emerald-100 text-emerald-800" },
    failed: { label: "Failed", cls: "bg-rose-100 text-rose-800" },
    skipped_no_email: { label: "No email", cls: "bg-amber-100 text-amber-800" },
  };
  const cfg = map[status] || { label: status, cls: "bg-zinc-100" };
  return <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-medium ${cfg.cls}`}>{cfg.label}</span>;
}

// ---------------------------------------------------------------------------
// Payroll preview section
// ---------------------------------------------------------------------------
function PayrollPreviewSection({ month, setMonth, onLoad, loading, preview }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-sm" data-testid="payroll-preview-section">
      <div className="p-5 border-b border-zinc-100 flex items-center gap-3 flex-wrap">
        <Eye size={18} className="text-emerald-700" />
        <div>
          <h2 className="font-display text-2xl font-bold">Next payroll preview</h2>
          <p className="text-xs text-zinc-500 mt-1">
            Dry-run what payroll will deduct — uses each employee's elected regime + declarations · Full-day attendance assumed
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Label className="text-xs text-zinc-500">Period</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="w-36 rounded-sm" data-testid="preview-month-picker" />
          <Button onClick={onLoad} disabled={loading}
            className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm"
            data-testid="preview-load-btn">
            <Eye size={14} className="mr-1.5" />
            {loading ? "Computing…" : (preview ? "Refresh" : "Preview")}
          </Button>
        </div>
      </div>

      {!preview && !loading && (
        <div className="p-12 text-center text-sm text-zinc-500" data-testid="preview-empty">
          <Eye size={32} weight="thin" className="mx-auto text-zinc-300 mb-2" />
          Click <span className="font-medium text-zinc-700">Preview</span> to see what the {MONTH_LABEL(month)} payroll will deduct for every employee.
        </div>
      )}

      {loading && (
        <div className="p-12 text-center text-sm text-zinc-500">Computing preview…</div>
      )}

      {preview && (
        <>
          {/* Summary strip */}
          <div className="p-4 bg-emerald-50/40 border-b border-zinc-100 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm" data-testid="preview-summary">
            <PreviewStat label="Employees" value={preview.employees} />
            <PreviewStat label="Gross payout" value={fmtL(preview.totals.gross)} accent />
            <PreviewStat label="Total TDS" value={fmtL(preview.totals.tds)} accent />
            <PreviewStat label="Net payout" value={fmtL(preview.totals.net_pay)} accent />
            <PreviewStat label="Cost to company" value={fmtL(preview.totals.ctc)} accent />
          </div>

          {/* Source breakdown + already-run warning */}
          <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-4 flex-wrap text-xs">
            <span className="text-zinc-500">TDS derived from:</span>
            {Object.entries(preview.tds_source_counts || {}).map(([src, count]) => count > 0 && (
              <Badge key={src} variant="outline"
                className={`rounded-sm text-[10px] ${SOURCE_BADGE[src]?.color || "bg-zinc-50"}`}>
                {SOURCE_BADGE[src]?.label || src} · {count}
              </Badge>
            ))}
            {preview.already_run && (
              <Badge variant="outline" className="rounded-sm bg-rose-50 text-rose-800 border-rose-200 ml-auto">
                <Warning size={12} className="mr-1" weight="fill" />
                Payroll already run for {MONTH_LABEL(preview.period_month)}
              </Badge>
            )}
          </div>

          {/* Rows table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="preview-table">
              <thead className="bg-zinc-50 border-b border-zinc-200 text-xs text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Employee</th>
                  <th className="text-right px-3 py-2">Gross</th>
                  <th className="text-right px-3 py-2">PF (E)</th>
                  <th className="text-right px-3 py-2">ESIC (E)</th>
                  <th className="text-right px-3 py-2">PT</th>
                  <th className="text-right px-3 py-2">TDS</th>
                  <th className="text-left px-3 py-2">TDS source</th>
                  <th className="text-right px-3 py-2">Other</th>
                  <th className="text-right px-3 py-2">Net pay</th>
                  <th className="text-right px-3 py-2">CTC</th>
                </tr>
              </thead>
              <tbody className="font-mono-plex">
                {preview.rows.map((r) => (
                  <tr key={r.employee_id} className="border-t border-zinc-100 hover:bg-zinc-50/50"
                    data-testid={`preview-row-${r.employee_id}`}>
                    <td className="px-3 py-2 font-sans">
                      <div className="font-medium text-zinc-900">{r.employee_name}</div>
                      <div className="text-[10px] text-zinc-500">{r.employee_code}{r.department ? ` · ${r.department}` : ""}</div>
                    </td>
                    <td className="px-3 py-2 text-right">{fmt(r.gross)}</td>
                    <td className="px-3 py-2 text-right text-zinc-500">{fmt(r.employee_pf)}</td>
                    <td className="px-3 py-2 text-right text-zinc-500">{fmt(r.employee_esic)}</td>
                    <td className="px-3 py-2 text-right text-zinc-500">{fmt(r.professional_tax)}</td>
                    <td className="px-3 py-2 text-right font-medium text-emerald-800"
                      data-testid={`preview-tds-${r.employee_id}`}>{fmt(r.tds)}</td>
                    <td className="px-3 py-2 font-sans">
                      <Badge variant="outline" className={`rounded-sm text-[9px] ${SOURCE_BADGE[r.tds_source]?.color || "bg-zinc-50"}`}>
                        {SOURCE_BADGE[r.tds_source]?.label || r.tds_source}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right text-zinc-500">{fmt(r.other)}</td>
                    <td className="px-3 py-2 text-right font-medium">{fmt(r.net_pay)}</td>
                    <td className="px-3 py-2 text-right text-zinc-500">{fmt(r.ctc)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-zinc-50 border-t-2 border-zinc-200 font-medium">
                <tr>
                  <td className="px-3 py-2 uppercase tracking-widest text-[10px] text-zinc-500 font-sans">
                    Total ({preview.employees})
                  </td>
                  <td className="px-3 py-2 text-right font-mono-plex">{fmt(preview.totals.gross)}</td>
                  <td className="px-3 py-2 text-right font-mono-plex text-zinc-500">{fmt(preview.totals.employee_pf)}</td>
                  <td className="px-3 py-2 text-right font-mono-plex text-zinc-500">{fmt(preview.totals.employee_esic)}</td>
                  <td className="px-3 py-2 text-right font-mono-plex text-zinc-500">{fmt(preview.totals.professional_tax)}</td>
                  <td className="px-3 py-2 text-right font-mono-plex text-emerald-800">{fmt(preview.totals.tds)}</td>
                  <td />
                  <td className="px-3 py-2 text-right font-mono-plex text-zinc-500">{fmt(preview.totals.other)}</td>
                  <td className="px-3 py-2 text-right font-mono-plex">{fmt(preview.totals.net_pay)}</td>
                  <td className="px-3 py-2 text-right font-mono-plex text-zinc-500">{fmt(preview.totals.ctc)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="p-4 text-[11px] text-zinc-500 border-t border-zinc-100 space-y-1">
            <div>• Preview computed on {preview.working_days_basis} working days · {preview.attendance_assumption}. Real payroll prorates by actual attendance.</div>
            <div>• "TDS source" tells you exactly how each employee's tax was derived — elected regime + declarations (accurate) or fallback flat percent (needs election).</div>
            <div>• No records saved. Run the actual payroll from Payroll → Payroll runs when the period ends.</div>
          </div>
        </>
      )}
    </div>
  );
}

function PreviewStat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-lg font-bold ${accent ? "text-emerald-800" : "text-zinc-900"}`}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row + presentational sub-components (unchanged from v1.5.3)
// ---------------------------------------------------------------------------
function TaxRow({ r, expanded, onToggle }) {
  const hasElection = !!r.regime;
  return (
    <>
      <tr className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`tax-row-${r.employee_id}`}>
        <td className="px-2 py-2 align-top">
          {hasElection && (
            <button onClick={onToggle} data-testid={`tax-expand-${r.employee_id}`}
              className="text-zinc-400 hover:text-zinc-700 p-1">
              {expanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
            </button>
          )}
        </td>
        <td className="px-3 py-2 font-sans">
          <div className="font-medium text-zinc-900">{r.employee_name || "—"}</div>
          <div className="text-[10px] text-zinc-500">{r.employee_code}{r.department ? ` · ${r.department}` : ""}</div>
        </td>
        <td className="px-3 py-2 font-sans">
          {hasElection ? (
            <Badge className={`rounded-sm text-[10px] uppercase ${r.regime === "new" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-indigo-100 text-indigo-800 border-indigo-200"}`}
              variant="outline" data-testid={`tax-regime-${r.employee_id}`}>
              {r.regime}
            </Badge>
          ) : (
            <Badge variant="outline" className="rounded-sm bg-amber-50 text-amber-800 border-amber-200 text-[10px]"
              data-testid={`tax-regime-${r.employee_id}`}>
              Not elected
            </Badge>
          )}
        </td>
        <td className="px-3 py-2 text-right">{fmt(r.annual_gross)}</td>
        <td className="px-3 py-2 text-right text-zinc-500">{fmt(r.total_deductions)}</td>
        <td className="px-3 py-2 text-right">{fmt(r.taxable_income)}</td>
        <td className="px-3 py-2 text-right font-medium">{fmt(r.annual_tax)}</td>
        <td className="px-3 py-2 text-right font-medium text-emerald-800" data-testid={`tax-tds-${r.employee_id}`}>{fmt(r.monthly_tds)}</td>
        <td className="px-3 py-2 text-right text-zinc-500">{fmt(r.ytd_tds)}</td>
        <td className="px-3 py-2 text-[10px] text-zinc-500 font-sans">
          {r.elected_at ? new Date(r.elected_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}
        </td>
      </tr>
      {hasElection && expanded && (
        <tr className="bg-zinc-50/70 border-t border-zinc-100" data-testid={`tax-details-${r.employee_id}`}>
          <td />
          <td colSpan={9} className="px-3 py-3">
            <DeclarationsBlock decls={r.declarations || {}} regime={r.regime} />
          </td>
        </tr>
      )}
    </>
  );
}

function DeclarationsBlock({ decls, regime }) {
  const ch = [
    ["80C", decls.d80c], ["80CCD(1B)", decls.d80ccd_1b], ["80CCD(2)", decls.d80ccd_2],
    ["80D self", decls.d80d_self], ["80D parents", decls.d80d_parents],
    ["80E", decls.d80e], ["80G", decls.d80g], ["80GG", decls.d80gg],
    ["80TTA/TTB", decls.d80tta], ["80EEA", decls.d80eea],
    ["80DDB", decls.d80ddb], ["80DD", decls.d80dd], ["80U", decls.d80u],
  ];
  const ex = [
    ["HRA exempt", decls.hra_exempt],
    ["LTA exempt", decls.lta_exempt],
    ["24(b) home loan", decls.d24b],
    ["Family pension", decls.family_pension_deduction],
    ["Other exempt", decls.other_exempt],
  ];
  const nonZero = (arr) => arr.filter(([, v]) => v > 0);
  const chFiltered = nonZero(ch);
  const exFiltered = nonZero(ex);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-sans">
      <DeclarationGroup title="Chapter VI-A deductions"
        muted={regime === "new"}
        items={chFiltered.length ? chFiltered : [["—", 0]]} />
      <DeclarationGroup title="Section 10 exemptions & home loan"
        muted={regime === "new"}
        items={exFiltered.length ? exFiltered : [["—", 0]]} />
      {regime === "new" && (chFiltered.length + exFiltered.length) > 0 && (
        <div className="md:col-span-2 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded-sm p-2">
          <Warning size={12} weight="fill" className="inline mr-1" />
          Employee elected the <b>new regime</b> — Chapter VI-A and §10 exemptions above are declared but <b>not applied</b> to their TDS. Standard deduction ₹75,000 &amp; 80CCD(2) still apply.
        </div>
      )}
    </div>
  );
}

function DeclarationGroup({ title, items, muted }) {
  return (
    <div className={`border border-zinc-200 rounded-sm bg-white p-3 ${muted ? "opacity-70" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-2 font-mono-plex">
        {items.map(([label, value]) => (
          <div key={label} className="flex justify-between text-[11px]">
            <span className="text-zinc-500 font-sans">{label}</span>
            <span className={muted ? "text-zinc-400 line-through" : "text-zinc-800"}>{fmt(value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Ic, accent, sub, testid }) {
  const accentClass = {
    emerald: "text-emerald-800 bg-emerald-50 border-emerald-200",
    amber: "text-amber-800 bg-amber-50 border-amber-200",
    indigo: "text-indigo-800 bg-indigo-50 border-indigo-200",
  }[accent] || "text-zinc-800 bg-white border-zinc-200";
  return (
    <div className={`rounded-sm border p-3 ${accentClass}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-70">
        <Ic size={12} /> {label}
      </div>
      <div className="font-display text-xl font-bold mt-1">{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-1">{sub}</div>}
    </div>
  );
}
